import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RequestStatus } from '@freelancer/datastore';
import {
  ContestFee,
  ContestFeesCollection,
  ContestUpgrade,
  ContestUpgradeCardOption,
  Currency,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  map,
  publishReplay,
  refCount,
  startWith,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'app-contest-upgrades',
  template: `
    <fl-heading
      i18n="Post Job Page contest upgrade field label"
      [headingType]="HeadingType.H3"
      [flMarginBottom]="Margin.XXXSMALL"
      [size]="TextSize.MID"
    >
      What type of contest do you want to run?
    </fl-heading>
    <fl-banner-alert
      *ngIf="isUrgentUpgradeSelected$ | async"
      i18n="
         Post Job Page compulsory urgent upgrade notice banner when urgent
        upgrade is selected
      "
      [flMarginBottom]="Margin.SMALL"
      [type]="BannerAlertType.INFO"
    >
      To ensure you get a fast result, this contest will be upgraded to
      Guaranteed automatically
    </fl-banner-alert>
    <fl-bit [flMarginBottom]="Margin.SMALL">
      <fl-grid [vAlign]="VerticalAlignment.VERTICAL_STRETCH">
        <fl-col
          class="BasicUpgrade-card"
          [col]="12"
          [colTablet]="6"
          [flMarginBottom]="Margin.SMALL"
          [flMarginBottomTablet]="Margin.NONE"
          [ngClass]="{ IsDisabled: isUrgentUpgradeSelected$ | async }"
        >
          <fl-radio-card
            i18n-pictureAlt="
               Post Job Page contest upgrade - icon for basic upgrade
            "
            flTrackingLabel="Basic"
            pictureAlt="Basic Contest"
            pictureSrc="job-post/contest-icon.svg"
            [control]="contestTypeControl"
            [isSelected]="isBasicCardSelected$ | async"
            [value]="ContestUpgradeCardOption.BASIC"
            (click)="cardSelected.emit(ContestUpgradeCardOption.BASIC)"
          >
            <fl-radio-card-body>
              <fl-text
                i18n="Post Job Page contest upgrade - choose basic contest"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Basic contest
              </fl-text>
              <fl-text
                *ngIf="!(isUrgentUpgradeSelected$ | async)"
                i18n="Post Job Page contest upgrade - basic contest description"
              >
                Start getting entries. Award your favorites. Money back
                guarantee.
              </fl-text>
              <fl-text
                *ngIf="isUrgentUpgradeSelected$ | async"
                i18n="
                   Post Job Page contest upgrade - basic contest description
                  with unavailable notice because urgent upgrade is selected
                "
              >
                Start getting entries. Award your favorites. Money back
                guarantee. Unavailable for urgent contests.
              </fl-text>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
        <fl-col
          class="ContestUpgrades-cardContainer"
          [col]="12"
          [colTablet]="6"
        >
          <fl-radio-card
            i18n-pictureAlt="
               Post Job Page contest upgrade - icon for guaranteed contest
            "
            flTrackingLabel="Guaranteed"
            pictureAlt="Guaranteed Contest"
            pictureSrc="job-post/fixed-price-project.svg"
            [control]="contestTypeControl"
            [isSelected]="isGuaranteedCardSelected$ | async"
            [value]="ContestUpgradeCardOption.GUARANTEED"
            (click)="cardSelected.emit(ContestUpgradeCardOption.GUARANTEED)"
          >
            <fl-radio-card-body>
              <fl-text
                i18n="Post Job Page contest upgrade - choose guaranteed contest"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Guaranteed contest
              </fl-text>
              <fl-text
                i18n="
                   Post Job Page contest upgrade - guaranteed contest
                  description
                "
              >
                Attract higher quality entries from freelancers. Moneyback
                guarantee is not applicable if a contest is guaranteed.
              </fl-text>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
      </fl-grid>
    </fl-bit>
    <fl-bit
      class="AdvancedOptions-link"
      [flMarginBottom]="
        upgradeFeesStatus?.error && showAdvanced ? Margin.XSMALL : Margin.LARGE
      "
    >
      <fl-link
        *ngIf="!showAdvanced"
        flTrackingLabel="ShowAdvanced"
        i18n="Post Job Page contest upgrade - show advanced upgrade options"
        [flMarginRight]="Margin.NONE"
        (click)="showAdvancedOptions()"
      >
        Show advanced options
      </fl-link>
      <fl-link
        *ngIf="showAdvanced"
        flTrackingLabel="HideAdvanced"
        i18n="Post Job Page contest upgrade - hide advanced upgrade options"
        [flMarginRight]="Margin.NONE"
        (click)="hideAdvancedOptions()"
      >
        Hide advanced options
      </fl-link>
    </fl-bit>

    <fl-banner-alert
      *ngIf="upgradeFeesStatus?.error && showAdvanced"
      i18n="Post Job Page contest upgrade fees update error message"
      [closeable]="false"
      [flMarginBottom]="Margin.XSMALL"
    >
      Can't update fees at the moment. You can always upgrade after posting your
      contest.
    </fl-banner-alert>

    <app-contest-advanced-upgrades
      *ngIf="showAdvanced"
      class="AdvancedOptions-content"
      [control]="control"
      [isLoadingFees]="!upgradeFeesStatus?.ready || upgradeFeesStatus?.error"
      [isUrgentUpgradeSelected]="isUrgentUpgradeSelected$ | async"
      [ngClass]="{ IsVisible: showAdvanced }"
      [selectedCurrency]="selectedCurrency"
      [upgradeFees]="upgradeFees"
    ></app-contest-advanced-upgrades>
  `,
  styleUrls: ['./upgrades.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestUpgradesComponent implements OnInit, OnChanges, OnDestroy {
  BannerAlertType = BannerAlertType;
  ContestUpgradeCardOption = ContestUpgradeCardOption;
  TextSize = TextSize;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  Margin = Margin;
  VerticalAlignment = VerticalAlignment;

  @Input() contestTypeControl: FormControl;
  @Input() control: FormControl;
  @Input() upgradeFees: ContestFee;
  @Input() upgradeFeesStatus: RequestStatus<ContestFeesCollection>;
  @Input() selectedCurrency: Currency;
  @Output() cardSelected: EventEmitter<
    ContestUpgradeCardOption
  > = new EventEmitter();
  @Output() advancedUpgradesClicked: EventEmitter<boolean> = new EventEmitter();

  showAdvanced = false;

  // Data stream
  isBasicCardSelected$: Rx.Observable<boolean>;
  isGuaranteedCardSelected$: Rx.Observable<boolean>;
  isUrgentUpgradeSelected$: Rx.Observable<boolean>;
  toggleGuaranteedCardSubscription?: Rx.Subscription;
  toggleGuaranteedUpgradeSubscription?: Rx.Subscription;

  ngOnInit() {
    if (this.upgradeFeesStatus && this.upgradeFeesStatus.error) {
      this.disableUpgrades();
    }

    this.isBasicCardSelected$ = this.contestTypeControl.valueChanges.pipe(
      startWith(this.contestTypeControl.value),
      map(contestType => contestType === ContestUpgradeCardOption.BASIC),
    );

    this.isGuaranteedCardSelected$ = this.contestTypeControl.valueChanges.pipe(
      startWith(this.contestTypeControl.value),
      map(contestType => contestType === ContestUpgradeCardOption.GUARANTEED),
    );

    this.isUrgentUpgradeSelected$ = this.control.valueChanges.pipe(
      map((upgrades: ContestUpgrade) => !!upgrades.urgent),
      distinctUntilChanged(),
      tap(isUrgent => {
        if (isUrgent) {
          this.control.patchValue({ guaranteed: true });
          this.contestTypeControl.setValue(ContestUpgradeCardOption.GUARANTEED);
          this.cardSelected.emit(ContestUpgradeCardOption.GUARANTEED);
        }
      }),
      publishReplay(1),
      refCount(),
    );

    this.toggleGuaranteedCardSubscription = this.control.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap((upgrades: ContestUpgrade) => {
          if (
            upgrades.guaranteed &&
            this.contestTypeControl.value !==
              ContestUpgradeCardOption.GUARANTEED
          ) {
            this.contestTypeControl.setValue(
              ContestUpgradeCardOption.GUARANTEED,
            );
          }

          if (
            !upgrades.guaranteed &&
            this.contestTypeControl.value ===
              ContestUpgradeCardOption.GUARANTEED
          ) {
            this.contestTypeControl.setValue('');
          }
        }),
      )
      .subscribe();

    this.toggleGuaranteedUpgradeSubscription = this.contestTypeControl.valueChanges
      .pipe(
        startWith(this.contestTypeControl.value),
        distinctUntilChanged(),
        tap((card: ContestUpgradeCardOption) => {
          if (
            card === ContestUpgradeCardOption.BASIC &&
            this.control.value.guaranteed
          ) {
            this.control.patchValue({ guaranteed: false });
          }

          if (
            card === ContestUpgradeCardOption.GUARANTEED &&
            !this.control.value.guaranteed
          ) {
            this.control.patchValue({ guaranteed: true });
          }
        }),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.upgradeFeesStatus &&
      changes.upgradeFeesStatus.currentValue &&
      changes.upgradeFeesStatus.previousValue
    ) {
      if (
        changes.upgradeFeesStatus.currentValue.error &&
        !changes.upgradeFeesStatus.previousValue.error
      ) {
        this.disableUpgrades();
      } else if (
        !changes.upgradeFeesStatus.currentValue.error &&
        changes.upgradeFeesStatus.previousValue.error
      ) {
        this.control.enable();
      }
    }
  }

  ngOnDestroy() {
    if (this.toggleGuaranteedCardSubscription) {
      this.toggleGuaranteedCardSubscription.unsubscribe();
    }
    if (this.toggleGuaranteedUpgradeSubscription) {
      this.toggleGuaranteedUpgradeSubscription.unsubscribe();
    }
  }

  showAdvancedOptions() {
    this.showAdvanced = true;
    this.advancedUpgradesClicked.emit(true);
  }

  hideAdvancedOptions() {
    this.showAdvanced = false;
    this.advancedUpgradesClicked.emit(false);
  }

  disableUpgrades() {
    const advancedGuaranteedControl = this.control.get('guaranteed');

    const guaranteedVal = advancedGuaranteedControl
      ? advancedGuaranteedControl.value
      : false;

    this.resetAdvanced();
    this.control.disable();

    if (advancedGuaranteedControl) {
      advancedGuaranteedControl.enable();
      this.control.patchValue({
        guaranteed: guaranteedVal,
      });
    }
  }

  resetAdvanced() {
    this.control.setValue({
      featured: false,
      guaranteed: this.control.value.guaranteed,
      highlight: false,
      nda: false,
      private: false,
      sealed: false,
      topContest: false,
      urgent: this.control.value.urgent,
    });
  }
}
